import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  FormControl,
  InputGroup,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Search as FiSearch, Gear as FiGear } from 'react-bootstrap-icons';
import Loading from '../shared/loading/Loading';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useLoading } from '../../hooks/useLoading';
import { loadConfigurationFactors } from '../../api/configuration';
import { ClientOrder } from '../../models/clientOrder';
import {
  convertClientOrderServer,
  loadClientOrders,
  rejectClientOrderServer,
} from '../../api/clientOrders';
import listStyles from '../shared/layout/dataListTable.module.scss';
import styles from '../quotations/Quotations.module.scss';
import ClientOrderRowActions from './ClientOrderRowActions';
import ClientOrderView from './ClientOrderView';
import ChannelInbox from './ChannelInbox';
import { getClientOrderReference } from './clientOrderReference';
import SubscriptionGuard from '../shared/billing/SubscriptionGuard';
import { Organization, SUPPORTED_CURRENCIES } from '../../models/organization';
import {
  formatDateEsFromFirestore,
  formatTimeEsFromFirestore,
} from '../../utils/format';
import { PANEL_PERMISSIONS } from '../../utils/panelPermissions';
import {
  PanelReadOnlyBanner,
} from '../auth/PanelWriteGate';
import { useCanWritePanelArea, usePanelAccess } from '../../contexts/PanelAccessContext';
import { isIntelligenceProductLive } from '../../config/intelligence';
import { useIntelligenceProductStatus } from '../../hooks/useIntelligenceProductStatus';

interface ClientOrdersProps {
  device: string;
  addToast: (message: string) => void;
  navStatus: string;
  organizationId: string;
  organization: Organization | null;
}

const STATUS_VARIANT: Record<string, string> = {
  submitted: 'primary',
  converted: 'success',
  rejected: 'danger',
  draft: 'secondary',
  cancelled: 'secondary',
};

const STATUS_LABEL: Record<string, string> = {
  submitted: 'Enviado',
  converted: 'Convertido',
  rejected: 'Rechazado',
  draft: 'Borrador',
  cancelled: 'Cancelado',
};

export default function ClientOrders({
  addToast,
  organizationId,
  organization,
}: ClientOrdersProps) {
  const { showConfirmation } = useConfirmationDialog();
  const { showLoading, hideLoading } = useLoading();
  const canWrite = useCanWritePanelArea(PANEL_PERMISSIONS.ORDERS);
  const { canView } = usePanelAccess();
  const { status: intelStatus } = useIntelligenceProductStatus(organizationId);
  const intelligenceLive = isIntelligenceProductLive(intelStatus);
  const canConfigureTelegram =
    canView(PANEL_PERMISSIONS.CONFIGURATION) && intelligenceLive;
  const [tab, setTab] = useState<'orders' | 'inbox'>('orders');
  const [orders, setOrders] = useState<ClientOrder[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [pending, setPending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selected, setSelected] = useState<ClientOrder | null>(null);
  const [referenceCurrency, setReferenceCurrency] = useState('USD');
  const [loadMore, setLoadMore] = useState(false);
  const [lastDocument, setLastDocument] = useState<any>(null);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [openConversationId, setOpenConversationId] = useState<string | null>(
    null
  );
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const getCurrencySymbol = (code?: string): string => {
    if (!code) return '';
    return SUPPORTED_CURRENCIES.find(c => c.code === code)?.symbol || code;
  };
  const formatMoney = (amount: number, code: string = referenceCurrency) => {
    const rounded = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
    const symbol = getCurrencySymbol(code);
    return symbol ? `${symbol} ${rounded}` : rounded;
  };

  const loadList = useCallback(async () => {
    if (!organizationId) return;
    const requestId = ++requestIdRef.current;
    setPending(true);
    try {
      const result = await loadClientOrders(
        organizationId,
        search || '',
        undefined,
        10,
        statusFilter || undefined
      );
      if (requestId !== requestIdRef.current) return;
      setOrders(result.clientOrders);
      setLoadMore(result.hasMore);
      setLastDocument(result.lastDoc);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      addToast('Error al cargar pedidos de clientes');
      setLoadMore(false);
    } finally {
      if (requestId === requestIdRef.current) setPending(false);
    }
  }, [organizationId, search, statusFilter, addToast]);

  useEffect(() => {
    mountedRef.current = true;
    setIsLoading(true);
    void loadConfigurationFactors(organizationId)
      .then(config => {
        if (mountedRef.current) {
          setReferenceCurrency(config.referenceCurrency || 'USD');
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, [organizationId]);

  useEffect(() => {
    if (tab === 'orders') void loadList();
  }, [loadList, tab]);

  const handleConvert = useCallback(
    (order: ClientOrder) => {
      showConfirmation(
        {
          title: 'Convertir a venta',
          message: `¿Convertir el pedido ${getClientOrderReference(
            order
          )} de ${order.clientName} en una venta? El pedido se conservará como convertido.`,
          confirmText: 'Convertir',
          cancelText: 'Cancelar',
        },
        async () => {
          if (!order.id) return;
          showLoading('Convirtiendo a venta');
          try {
            const sale = await convertClientOrderServer(organizationId, order.id);
            const saleRef = sale.saleNumber
              ? `#${sale.saleNumber}`
              : sale.id?.substring(0, 8) || '';
            addToast(
              saleRef
                ? `Venta ${saleRef} creada desde pedido`
                : 'Venta creada desde pedido'
            );
            setShowView(false);
            void loadList();
          } catch (e: any) {
            addToast(e?.message || 'Error al convertir');
          } finally {
            hideLoading();
          }
        }
      );
    },
    [addToast, showConfirmation, organizationId, loadList, showLoading, hideLoading]
  );

  const handleReject = useCallback(
    (order: ClientOrder) => {
      showConfirmation(
        {
          title: 'Rechazar pedido',
          message: `¿Rechazar el pedido ${getClientOrderReference(order)}?`,
          confirmText: 'Rechazar',
          cancelText: 'Cancelar',
        },
        async () => {
          if (!order.id) return;
          showLoading('Rechazando');
          try {
            await rejectClientOrderServer(organizationId, order.id);
            addToast('Pedido rechazado');
            setShowView(false);
            void loadList();
          } catch (e: any) {
            addToast(e?.message || 'Error al rechazar');
          } finally {
            hideLoading();
          }
        }
      );
    },
    [addToast, showConfirmation, organizationId, loadList, showLoading, hideLoading]
  );

  return (
    <Container fluid className="fade-in p-0 p-md-4">
      {isLoading ? <Loading /> : null}
      <SubscriptionGuard organization={organization}>
        <div className="white-card mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <h2 className="fw-bold mb-0">Pedidos de clientes</h2>
              <p className="text-secondary mb-0 mt-2">
                Solicitudes que llegan por Telegram. Conviértelas en ventas cuando
                estén listas.
              </p>
            </div>
            {canConfigureTelegram ? (
              <Link
                to="/configuration?tab=integrations&integration=telegram"
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
              >
                <FiGear />
                Configurar Telegram
              </Link>
            ) : null}
          </div>
          <div className="mt-3 d-flex gap-2">
            <Button
              variant={tab === 'orders' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setTab('orders')}
            >
              Pedidos
            </Button>
            <Button
              variant={tab === 'inbox' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setTab('inbox')}
            >
              Conversaciones
            </Button>
          </div>
        </div>

        {tab === 'inbox' ? (
          <ChannelInbox
            organizationId={organizationId}
            addToast={addToast}
            openConversationId={openConversationId}
          />
        ) : (
          <>
            <div className="white-card mb-4">
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FiSearch />
                    </InputGroup.Text>
                    <FormControl
                      type="text"
                      value={search}
                      placeholder="Buscar por cliente o número…"
                      onChange={e => setSearch(e.currentTarget.value)}
                      onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') void loadList();
                      }}
                    />
                    <Button variant="outline-secondary" onClick={() => void loadList()}>
                      Buscar
                    </Button>
                  </InputGroup>
                </Col>
                <Col xs={12} md={4}>
                  <FormControl
                    as="select"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.currentTarget.value)}
                  >
                    <option value="submitted">Enviados</option>
                    <option value="converted">Convertidos</option>
                    <option value="rejected">Rechazados</option>
                    <option value="">Todos</option>
                  </FormControl>
                </Col>
              </Row>
            </div>
            <PanelReadOnlyBanner area={PANEL_PERMISSIONS.ORDERS} />
            {pending ? (
              <Spinner animation="border" />
            ) : orders.length > 0 ? (
              <>
                <div className="d-block d-lg-none">
                  {orders.map((o, i) => (
                    <Card
                      key={o.id || i}
                      className={`mb-3 border-0 shadow-sm ${listStyles.clickableCard}`}
                      onClick={() => {
                        setSelected(o);
                        setShowView(true);
                      }}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between">
                          <div>
                            <div className={styles.referenceMain}>
                              {getClientOrderReference(o)}
                            </div>
                            <h6 className="mb-1 mt-1">{o.clientName}</h6>
                            <Badge bg={STATUS_VARIANT[o.status] || 'secondary'}>
                              {STATUS_LABEL[o.status] || o.status}
                            </Badge>
                            {o.paymentMethod ? (
                              <div className="small text-body-secondary mt-1">
                                {o.paymentMethod}
                                {o.paymentProofUrl ? ' · comprobante' : ''}
                              </div>
                            ) : null}
                          </div>
                          <div className={styles.totalCell}>
                            {formatMoney(o.total || 0)}
                          </div>
                        </div>
                        <div className="mt-2" onClick={e => e.stopPropagation()}>
                          <ClientOrderRowActions
                            order={o}
                            layout="mobile"
                            canWrite={canWrite}
                            onView={ord => {
                              setSelected(ord);
                              setShowView(true);
                            }}
                            onConvert={handleConvert}
                            onReject={handleReject}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
                <Card className="d-none d-lg-block border-0 shadow-sm">
                  <Table hover responsive className={`mb-0 ${listStyles.dataTable}`}>
                    <thead>
                      <tr>
                        <th>Ref.</th>
                        <th>Cliente</th>
                        <th>Estado</th>
                        <th>Pago</th>
                        <th>Fecha</th>
                        <th className="text-end">Total</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o, i) => (
                        <tr
                          key={o.id || i}
                          className={listStyles.clickableRow}
                          onClick={() => {
                            setSelected(o);
                            setShowView(true);
                          }}
                        >
                          <td className={styles.referenceMain}>
                            {getClientOrderReference(o)}
                          </td>
                          <td>{o.clientName}</td>
                          <td>
                            <Badge bg={STATUS_VARIANT[o.status] || 'secondary'}>
                              {STATUS_LABEL[o.status] || o.status}
                            </Badge>
                          </td>
                          <td>
                            <div>{o.paymentMethod || '—'}</div>
                            {o.paymentProofUrl ? (
                              <Badge bg="info" className="mt-1">
                                Comprobante
                              </Badge>
                            ) : null}
                          </td>
                          <td className={styles.dateCell}>
                            <div>{formatDateEsFromFirestore(o.created)}</div>
                            <div className={styles.dateTimeMeta}>
                              {formatTimeEsFromFirestore(o.created)}
                            </div>
                          </td>
                          <td className={`text-end ${styles.totalCell}`}>
                            {formatMoney(o.total || 0)}
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <ClientOrderRowActions
                              order={o}
                              canWrite={canWrite}
                              onView={ord => {
                                setSelected(ord);
                                setShowView(true);
                              }}
                              onConvert={handleConvert}
                              onReject={handleReject}
                              onOpenChat={ord => {
                                setOpenConversationId(ord.conversationId || null);
                                setTab('inbox');
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
                {loadMore ? (
                  <div className="d-flex justify-content-center mt-3">
                    <Button
                      variant="outline-primary"
                      disabled={loadMorePending}
                      onClick={async () => {
                        setLoadMorePending(true);
                        try {
                          const result = await loadClientOrders(
                            organizationId,
                            search,
                            lastDocument,
                            10,
                            statusFilter || undefined
                          );
                          setOrders(prev => [...prev, ...result.clientOrders]);
                          setLoadMore(result.hasMore);
                          setLastDocument(result.lastDoc);
                        } finally {
                          setLoadMorePending(false);
                        }
                      }}
                    >
                      {loadMorePending ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Cargar más'
                      )}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-secondary mb-0">
                No hay pedidos en este filtro.
                {canConfigureTelegram ? (
                  <>
                    {' '}
                    <Link to="/configuration?tab=integrations&integration=telegram">
                      Configurar Telegram
                    </Link>
                  </>
                ) : null}
              </p>
            )}
          </>
        )}

        <ClientOrderView
          show={showView}
          onClose={() => setShowView(false)}
          order={selected}
          organizationId={organizationId}
          referenceCurrency={referenceCurrency}
          canWrite={canWrite}
          onConvert={canWrite ? handleConvert : undefined}
          onReject={canWrite ? handleReject : undefined}
          onSaved={updated => {
            setSelected(updated);
            setOrders(prev =>
              prev.map(row => (row.id === updated.id ? updated : row))
            );
          }}
          addToast={addToast}
        />
      </SubscriptionGuard>
    </Container>
  );
}
